/* eslint-disable no-restricted-syntax */
import './Table.scss';
import './TableOpzV2.scss';

import { Box, Flex, Image, Skeleton, Text } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import FullScreenLoader from 'components/FullScreenLoader';
import { HeaderRowTemplate } from 'components/HeaderRow';
import PlanPermission from 'container/PlanPermission';
import { HEADER_HEIGHT_WITH_NAVIGATION } from 'projectConstants';
import {
  AddIndicator,
  DeleteIndicator,
  FetchBlockOutputsV2,
  GetDependencyIndicator,
  ReorderIndicator,
  UpdateIndicator,
  getAddIndicator,
  getBaseScenario,
  getDimensionalIndicatorV2,
  getIndicatorHeaders,
  getPlannerBlock,
  getPlannerBlockMetadata,
  getPlannerBlockMetadataBlockId,
  getPlannerBlockMetadataScenarioId,
  getPlannerBlockMetadataLoading,
  handleAddIndicator,
  getPlannerModel,
  getSelectedCell,
  getBlockOutputsLoadingV2,
  getBlockOutputsErrorV2,
  handleSelectedCellLocally,
} from 'redux/PlannerModeSlice';
import {
  appendDataTableDraftRow,
  getExandSideInputStatus,
  removeDataTableDraftRowById,
} from 'redux/BuilderModeSlice';
import { UpdateBlock } from 'redux/ModelsSlice';
import addAction from 'assets/add_action_icon.png';
import { ChevronCellTemplate } from 'components/ChevronCell/ChevronwithEsc';
import getContentBasedDimensionalColumns from 'pages/BuilderModeV1/utils/contentBasedColumnWidths';

import useDataTableRowsV2 from './dataTableV2/useDataTableRowsV2';
import {
  getDimensionalColumns,
  getExpandedRows,
  handleCanReorderRows,
  handleCellChange,
  handleColumnResize,
  reorderArray,
} from './TableHelpersOpzV2';
import { TextCellTemplateOpzV2 } from './TextCellTemplateOpzV2';

function getDimensionIdsString(plannerBlock: any): string {
  if (!plannerBlock?.dimensions) return '';
  return plannerBlock.dimensions
    .filter((item: any) => item.name !== 'Time')
    .map((item: any) => item.id)
    .join(', ');
}

const BuilderModeDataTableOpzV2 = ({
  planPermission = false,
  contentBasedColumnWidths = false,
  hideTableAddRow = false,
}: {
  planPermission?: boolean;
  contentBasedColumnWidths?: boolean;
  hideTableAddRow?: boolean;
} = {}) => {
  const { blockId } = useParams();
  const dispatch: any = useDispatch();
  const tableReference: any = useRef(null);

  // Avoid TS "union too complex" with Chakra polymorphic components
  const FlexContainer = Flex as any;
  const TextContainer = Text as any;
  const SkeletonContainer = Skeleton as any;
  const ImageContainer = Image as any;

  const selectedCell = useSelector(getSelectedCell);
  const plannerBlock = useSelector(getPlannerBlock);
  const plannerBlockMetadata = useSelector(getPlannerBlockMetadata);
  const blockMetadataScenarioId = useSelector(getPlannerBlockMetadataScenarioId);
  const blockMetadataBlockId = useSelector(getPlannerBlockMetadataBlockId);
  const blockMetadataLoading = useSelector(getPlannerBlockMetadataLoading);
  const baseScenario = useSelector(getBaseScenario);
  const addIndicator = useSelector(getAddIndicator);
  const modelDetails = useSelector(getPlannerModel);
  const indicatorHeaders = useSelector(getIndicatorHeaders);

  const indicatorValues = useSelector(getDimensionalIndicatorV2);
  const isExpand = useSelector(getExandSideInputStatus);
  const blockOutputsLoading = useSelector(getBlockOutputsLoadingV2);
  const blockOutputsError = useSelector(getBlockOutputsErrorV2);

  const metadataIndicators = plannerBlockMetadata?.indicators ?? [];

  const [deleteModal, setDeleteModal] = useState({
    indicatorId: '',
    indicatorName: '',
    status: false,
  });
  const [gridProperties, setGridProperties] = useState({});
  const [addRowWorker, setAddRowWorker] = useState<Worker | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const clickCountReference = useRef(0);
  const [isQueueProcessing, setIsQueueProcessing] = useState(false);
  const messageQueueReference = useRef<any[]>([]);
  const fetchIndicatorsRequestReference = useRef<NodeJS.Timeout>();
  const processingTimeoutReference = useRef<NodeJS.Timeout>();
  const baseScenarioReference = useRef(baseScenario);

  const hasOutputsData = (indicatorValues?.length ?? 0) > 0;
  const valuesLoading = blockOutputsLoading && !hasOutputsData;

  const { tableData, rows: derivedRows } = useDataTableRowsV2({
    indicatorValues,
    metadataIndicators,
    indicatorHeaders,
    modelReportingCurrency: modelDetails?.reporting_currency,
    valuesLoading,
    blockId,
    scenarioId: baseScenario?.id,
    includeFooterRow: !hideTableAddRow,
  });
  const memorizedColumns: any = useMemo(() => {
    if (!indicatorHeaders?.length || !plannerBlock) return [];

    if (contentBasedColumnWidths && derivedRows?.length) {
      return getContentBasedDimensionalColumns(indicatorHeaders, derivedRows);
    }

    return getDimensionalColumns(
      indicatorHeaders,
      tableReference?.current?.offsetWidth,
      plannerBlock,
    );
  }, [indicatorHeaders, plannerBlock, contentBasedColumnWidths, derivedRows, isExpand]);

  const [columns, setColumns]: any = useState(memorizedColumns);
  const [rows, setRows]: any = useState(derivedRows);
  const rowsToRender = useMemo(() => getExpandedRows(rows), [rows]);

  const createEmptyRow = useMemo(() => {
    if (!indicatorHeaders?.length) return {};

    const object: Record<string, number> = indicatorHeaders.reduce(
      (accumulator: Record<string, number>, header: any) => {
        if (header?.name) {
          accumulator[header.name] = 0;
        }
        return accumulator;
      },
      {},
    );
    return object;
  }, [indicatorHeaders]);

  useEffect(() => {
    setRows(derivedRows);
  }, [derivedRows]);

  useEffect(() => {
    if (memorizedColumns?.length >= 0) setColumns(memorizedColumns);
  }, [memorizedColumns]);

  useEffect(() => {
    if (contentBasedColumnWidths) {
      if (derivedRows?.length && indicatorHeaders?.length) {
        setColumns(getContentBasedDimensionalColumns(indicatorHeaders, derivedRows));
      }
      return;
    }

    if (tableReference?.current && indicatorHeaders?.length >= 0 && plannerBlock) {
      setColumns(
        getDimensionalColumns(indicatorHeaders, tableReference?.current?.offsetWidth, plannerBlock),
      );
    }
  }, [
    contentBasedColumnWidths,
    derivedRows,
    indicatorHeaders,
    plannerBlock,
    tableReference?.current,
  ]);

  const processUpdates = useCallback(
    (updates: any[]) => {
      if (updates.length === 0) return;

      requestAnimationFrame(() => {
        updates.forEach((update) => {
          dispatch(appendDataTableDraftRow(update.baseRow));
        });

        setTimeout(() => {
          const lastUpdate = updates[updates.length - 1];
          if (lastUpdate?.rowId) {
            setGridProperties({
              focusLocation: { rowId: lastUpdate.rowId, columnId: 'name' },
            });
          }
        }, 50);

        dispatch(handleAddIndicator(false));
        setIsProcessing(false);
        clickCountReference.current = 0;
      });
    },
    [dispatch],
  );

  const processMessageQueue = useCallback(() => {
    if (messageQueueReference.current.length === 0) {
      setIsQueueProcessing(false);
      setIsProcessing(false);
      return;
    }

    const updates = [...messageQueueReference.current];
    messageQueueReference.current = [];

    processUpdates(updates);
    setIsQueueProcessing(false);
  }, [processUpdates]);

  useEffect(() => {
    const worker = new Worker(new URL('../../workers/addRowWorker.ts', import.meta.url), {
      type: 'module',
    });

    worker.addEventListener('message', (event) => {
      messageQueueReference.current.push(event.data);

      if (!isQueueProcessing) {
        setIsQueueProcessing(true);
        requestAnimationFrame(processMessageQueue);
      }
    });

    setAddRowWorker(worker);

    return () => {
      worker.terminate();
    };
  }, [isQueueProcessing, processMessageQueue]);

  const handleAddRow = useCallback(() => {
    if (!addRowWorker || isProcessing) return;

    const currentClickCount = clickCountReference.current + 1;
    clickCountReference.current = currentClickCount;
    setIsProcessing(true);

    setGridProperties({});

    if (processingTimeoutReference.current) {
      clearTimeout(processingTimeoutReference.current);
    }

    addRowWorker.postMessage({
      tableData,
      createEmptyRow,
      indicatorHeaders,
    });

    processingTimeoutReference.current = setTimeout(() => {
      if (clickCountReference.current === currentClickCount) {
        setIsProcessing(false);
      }
    }, 1000);
  }, [addRowWorker, tableData, createEmptyRow, indicatorHeaders, isProcessing]);

  useEffect(() => {
    return () => {
      if (processingTimeoutReference.current) {
        clearTimeout(processingTimeoutReference.current);
      }
    };
  }, []);

  useEffect(() => {
    if (addIndicator && addRowWorker) {
      handleAddRow();
    }
  }, [addIndicator, addRowWorker, handleAddRow]);

  useEffect(() => {
    baseScenarioReference.current = baseScenario;
  }, [baseScenario]);

  const handleDeleteRow = async (data: any) => {
    const rowToDelete = rows.find((row: any) => row.itemId === data?.id);
    if (rowToDelete?.isNewCell) {
      dispatch(removeDataTableDraftRowById(data?.id));
      return;
    }
    await dispatch(GetDependencyIndicator({ indicatorId: data?.id }));
    setDeleteModal({ indicatorId: data?.id, indicatorName: data?.itemName, status: true });
  };

  const handleFocusLocationChanged = async (data: any) => {
    if (selectedCell?.rowId === data?.rowId) return;

    const sourceRow = tableData.find((item: any) => {
      const itemRowId = item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`;
      return (
        String(itemRowId) === String(data?.rowId) ||
        Number(item?.id) === Number(data?.rowId) ||
        Number(item?.indicatorId) === Number(data?.rowId)
      );
    });

    await dispatch(
      handleSelectedCellLocally({
        ...data,
        ...(sourceRow && {}),
        id: sourceRow?.parentId ?? sourceRow?.id ?? data?.rowId,
        parentId: sourceRow?.parentId,
        indicatorId: sourceRow?.indicatorId ?? sourceRow?.parentId ?? sourceRow?.id,
      }),
    );
  };

  const fetchBlockOutputsIndicators = useCallback(() => {
    const currentBaseScenario = baseScenarioReference.current;
    if (!currentBaseScenario?.id || !blockId) return;

    const dimensionIds = getDimensionIdsString(plannerBlock);
    const payload = {
      blockId,
      params: {
        dim_id: dimensionIds,
        indicator_filter: 'all',
        scenario_id: currentBaseScenario.id,
      },
    };
    dispatch(FetchBlockOutputsV2(payload));
  }, [blockId, plannerBlock, dispatch]);

  const debouncedFetchIndicators = useCallback(() => {
    if (fetchIndicatorsRequestReference.current) {
      clearTimeout(fetchIndicatorsRequestReference.current);
    }
    fetchIndicatorsRequestReference.current = setTimeout(() => {
      fetchBlockOutputsIndicators();
    }, 2000);
  }, [fetchBlockOutputsIndicators]);

  useEffect(() => {
    return () => {
      if (fetchIndicatorsRequestReference.current) {
        clearTimeout(fetchIndicatorsRequestReference.current);
      }
    };
  }, []);

  const metadataReady =
    !blockMetadataLoading &&
    plannerBlockMetadata != null &&
    blockMetadataScenarioId === baseScenario?.id &&
    blockMetadataBlockId === Number(blockId);

  useEffect(() => {
    if (baseScenario?.id && metadataReady) {
      fetchBlockOutputsIndicators();
    }
  }, [baseScenario?.id, metadataReady, fetchBlockOutputsIndicators, blockId]);

  const handleAddDimIndicator = useCallback(
    (indicatorName: string, committedDraftRowId: number | string) => {
      dispatch(
        AddIndicator({
          blockId,
          data: {
            name: indicatorName,
            type: 'Input',
          },
          toaster: true,
          committedDraftRowId,
        }),
      ).then((response: any) => {
        if (!response?.error) {
          dispatch(handleAddIndicator(false));
          setGridProperties({
            focusLocation: { rowId: response.payload.id, columnId: 'name' },
          });
        }
      });
    },
    [blockId, dispatch],
  );

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    const to = tableData?.findIndex((person: any) => person.id === targetRowId);
    const rowsIds = rowIds.map((id) => tableData?.findIndex((person: any) => person.id === id));
    const reorededArray = reorderArray(tableData, rowsIds, to);
    dispatch(
      ReorderIndicator({
        blockId,
        data: { indicators: reorededArray },
      }),
    ).then((response: any) => {
      if (!response?.error) debouncedFetchIndicators();
    });
  };

  const updateIndicator = useCallback(
    (changes: any, change: any) => {
      dispatch(
        UpdateIndicator({
          indicatorId: changes[0]?.rowId,
          data: {
            name: change?.extraKey
              ? change?.checked
                ? 'subtotal'
                : 'default'
              : change?.checked !== undefined
              ? `${change?.checked}`
              : change?.text,
          },
          toaster: {
            errorMessage: '"An indicator with this already exists',
          },
        }),
      );
    },
    [dispatch],
  );

  const onCellChange = (changes: any, columns_: any, setRows_: any) => {
    const change = changes[0];
    if (change?.type === 'text' && change?.newCell?.escapePressed) {
      setGridProperties({});
      return;
    }
    if (change?.newCell?.isNewCell) {
      handleAddDimIndicator(change?.newCell?.text, change?.rowId);
    } else if (change?.newCell?.text !== change?.previousCell?.text) {
      updateIndicator(changes, change?.newCell);
    } else if (change?.type === 'chevron') {
      handleCellChange(changes, columns_, setRows_);
    }
  };

  const memoizedHandleContextMenu = useCallback(
    (
      selectedRowIds: string[],
      selectedColIds: string[],
      selectionMode: string,
      menuOptions: Array<{ id: string; label: string; handler?: () => void }>,
      selectedRanges: Array<Array<{ rowId: string; columnId: string }>>,
    ) => {
      if (
        selectedRanges[0] &&
        selectedRanges[0][0]?.rowId !== 'header' &&
        selectedRanges[0][0]?.rowId !== 'footer'
      ) {
        const deleteRow = {
          id: 'deleteRow',
          label: 'Delete row',
          handler: () => {
            const myRow = rows?.filter((data2: any) => {
              return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
            });
            handleDeleteRow({ id: myRow[0]?.itemId, itemName: myRow[0]?.itemName });
          },
        };
        let selectedRows = false;
        for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
          selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
        }
        if (selectedRanges[0][0]?.columnId !== 'delete' && selectedRows) {
          const filteredOptions = menuOptions.filter((object) => object.id !== 'cut');
          return [...filteredOptions, deleteRow];
        }
        return selectedRanges[0][0]?.columnId === 'delete' && selectedRanges[0]?.length === 1
          ? [deleteRow]
          : menuOptions.filter((object) => object.id !== 'cut');
      }
      return [];
    },
    [rows, handleDeleteRow],
  );

  const memoizedHandleCellChange = useCallback(
    (changes: any) => {
      onCellChange(changes, columns, setRows);
    },
    [columns, setRows, onCellChange],
  );

  const memoizedHandleColumnResize = useCallback(
    (ci: any, width: any) => {
      handleColumnResize(ci, width, setColumns);
      setTimeout(() => {
        dispatch(
          UpdateBlock({
            blockId,
            data: { indicator_column_size: Math.floor(width) },
          }),
        );
      }, 2000);
    },
    [blockId, dispatch, setColumns],
  );

  const handleLocationChanged = () => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
    return true;
  };

  useEffect(() => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
  }, [rows, columns]);

  const memoizedColumns = useMemo(() => {
    return columns;
  }, [columns]);

  const handleDeleteModal = () => {
    setDeleteModal({ indicatorId: '', indicatorName: '', status: false });
    dispatch(
      DeleteIndicator({
        indicatorId: deleteModal.indicatorId,
        toaster: {
          successMessage: 'Indicator deleted',
        },
      }),
    ).then((response: any) => {
      if (!response?.error) {
        setGridProperties({
          focusLocation: { rowId: 'header', columnId: 'name' },
        });
        fetchBlockOutputsIndicators();
      }
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (tableReference.current && !tableReference.current.contains(target)) {
        const isMenuClick =
          target.closest('[role="menu"]') ||
          target.closest('[role="menuitem"]') ||
          target.closest('[data-menu]') ||
          target.closest('button[aria-haspopup="menu"]') ||
          target.closest('[data-chakra-menu]') ||
          target.closest('button[role="switch"]') ||
          (target.closest('input[type="checkbox"]') && target.closest('[role="menuitem"]'));

        if (!isMenuClick) {
          debouncedFetchIndicators();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tableReference, debouncedFetchIndicators]);

  const showInitialSkeleton =
    rows?.length === 0 &&
    (blockMetadataLoading || (blockOutputsLoading && metadataIndicators.length === 0));

  if (showInitialSkeleton) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' pb='1'>
          {[...new Array(6)].map((_, index) => (
            <FlexContainer key={index} p={2} justifyContent={'space-between'}>
              {[...new Array(4)].map((__, subIndex: number) => (
                <Box key={subIndex} flex={1} mr={2}>
                  <SkeletonContainer
                    startColor='white'
                    endColor='#CCD4E8'
                    height='35px'
                    width='100%'
                  />
                </Box>
              ))}
            </FlexContainer>
          ))}
        </FlexContainer>
      </React.Fragment>
    );
  }

  if (blockOutputsError) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <TextContainer color='red.500'>{blockOutputsError}</TextContainer>
        </FlexContainer>
      </React.Fragment>
    );
  }

  // If metadata exists but V2 outputs came back empty, show an explicit empty state
  // instead of rendering metadata-only placeholder rows.
  if (
    metadataIndicators.length > 0 &&
    !blockOutputsLoading &&
    !hasOutputsData &&
    (rows?.length ?? 0) === 0
  ) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <TextContainer>No Data Found</TextContainer>
        </FlexContainer>
      </React.Fragment>
    );
  }

  if (!hasOutputsData && rows?.length === 0 && metadataIndicators.length === 0) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <Box
            onClick={() => handleAddRow()}
            width={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            flexDirection={'column'}
            gap={2}
          >
            <ImageContainer h={30} w={30} src={addAction} transform='scaleX(-1)' />
            <TextContainer
              color='white'
              cursor='pointer'
              textDecoration='underline'
              fontSize={'14px'}
            >
              Please click here to add indicator
            </TextContainer>
          </Box>
        </FlexContainer>
      </React.Fragment>
    );
  }

  const isTableStructureLoading =
    !columns?.length || !rowsToRender?.length || !indicatorHeaders?.length;

  if (isTableStructureLoading) {
    return (
      <React.Fragment>
        <FullScreenLoader
          width='100%'
          height={`calc(100vh - ${HEADER_HEIGHT_WITH_NAVIGATION}) - 45px`}
          bgColor='transparent'
        />
      </React.Fragment>
    );
  }

  return (
    <FlexContainer
      height={'100%'}
      width={'100%'}
      flexDirection={'column'}
      ref={tableReference}
      flex={1}
      overflow={'hidden'}
    >
      {columns?.length && rows?.length && rowsToRender?.length && (
        <Box
          className='block-table-container sm-scroll'
          width={'100%'}
          margin={'initial'}
          maxH={'initial'}
          overflowY={rows.length > 6 ? 'auto' : 'initial'}
        >
          <ReactGrid
            rows={rowsToRender}
            columns={memoizedColumns}
            enableRowSelection={rows?.length > 3}
            stickyTopRows={rows?.length <= 6 ? 0 : 1}
            stickyRightColumns={columns?.length <= 2 ? 0 : 1}
            stickyLeftColumns={columns?.length <= 2 ? 0 : 1}
            stickyBottomRows={hideTableAddRow ? 0 : rows?.length <= 3 ? 0 : 1}
            verticalStickyBreakpoint={100}
            horizontalStickyBreakpoint={100}
            onRowsReordered={handleRowsReorder}
            canReorderRows={handleCanReorderRows}
            {...gridProperties}
            onFocusLocationChanging={handleLocationChanged}
            onFocusLocationChanged={handleFocusLocationChanged}
            onCellsChanged={memoizedHandleCellChange}
            onContextMenu={planPermission ? undefined : (memoizedHandleContextMenu as any)}
            onColumnResized={planPermission ? undefined : memoizedHandleColumnResize}
            customCellTemplates={{
              header: new HeaderRowTemplate(),
              delete: new DeleteRowCellTemplate(),
              text: new TextCellTemplateOpzV2(),
              date: new DateCellTemplate(),
              addRow: new AddRowCellTemplate(handleAddRow, planPermission, isProcessing),
              chevron: new ChevronCellTemplate(planPermission),
              dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
            }}
          />
        </Box>
      )}
      <DeleteGroupModal
        isOpen={deleteModal.status}
        onClose={() => setDeleteModal({ indicatorId: '', indicatorName: '', status: false })}
        itemName={deleteModal.indicatorName || 'some name'}
        onDelete={() => handleDeleteModal()}
        orgChart={true}
        isBuilderMode={true}
      />
    </FlexContainer>
  );
};

export default PlanPermission(BuilderModeDataTableOpzV2);
